import { Component, Inject, OnDestroy } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgxImageCompressService } from 'ngx-image-compress';
import { TranslateService } from '@ngx-translate/core';
import { concat, Observable, of, Subject } from 'rxjs';
import { catchError, switchMap, takeUntil, tap, toArray } from 'rxjs/operators';
import { Sensor } from 'src/app/model/sensor';
import { AttachmentThermalService } from 'src/app/services/attachmentThermal.service';
import { SnackBarService } from 'src/app/services/snackBar.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { PhotoUtils } from 'src/app/utils/photoUtils';

export interface DialogDataMassiveUpload {
  sensors: Sensor[];
  type: string;
}

@Component({
    selector: 'app-execution-edit-massive-upload',
    templateUrl: './execution-edit-massive-upload.component.html',
    standalone: false,
})
export class ExecutionEditMassiveUploadComponent implements OnDestroy {

  checked = new Map<string, boolean>();

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<ExecutionEditMassiveUploadComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogDataMassiveUpload,
    private attachmentThermalService: AttachmentThermalService,
    private imageCompress: NgxImageCompressService,
    private snackBarService: SnackBarService,
    private translate: TranslateService,
    private spinnerService: SpinnerService) { }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isSelected(item: Sensor): boolean {
    if (item == null) {
      return false;
    }

    return this.checked.get(`${item.id}`);
  }

  isAllSelected(): boolean {
    return this.data.sensors.every(item => this.isSelected(item));
  }

  onCheckAllItems(event: MatCheckboxChange): void {
    this.data.sensors.forEach(elem => {
      this.checked.set(`${elem.id}`, event.checked);
    });
  }

  onSensorCheck(event: MatCheckboxChange): void {
    this.checked.set(`${event.source.value}`, event.checked);
  }

  disableUploadButton(): boolean {
    const checks = [];

    this.checked.forEach((value, key) => {
      if (value) {
        checks.push(key);
      }
    });

    return checks.length === 0;
  }

  accept(): string {
    let res = '*';

    const type = this.data.type;

    if (type === 'photo') {
      res = 'image/*';
    } else if (type === 'excel') {
      res = '.xlsx, .xls, .csv, .pdf';
    }

    return res;
  }

  uploadMassive(event): void {
    this.spinnerService.show();

    const checks: string[] = [];

    this.checked.forEach((value, key) => {
      if (value) {
        checks.push(key);
      }
    });

    if (checks.length === 0) {
      this.spinnerService.hide();

      return;
    }

    const type = this.data.type;
    const file = event.target.files[0] as File;

    const subidas = checks
      .map(id => ({ id, obs: this.uploadOne(type, +id, file) }))
      .filter(u => u.obs != null);

    if (subidas.length === 0) {
      this.spinnerService.hide();

      return;
    }

    const fallos: string[] = [];

    // De una en una, no en paralelo. Todas las sondas de una ejecucion escriben sobre
    // la misma fila de execution, asi que lanzarlas a la vez solo produce contencion
    // en la base; era la mitad del interbloqueo de PROD del 29-sep.
    //
    // Y cada una se resuelve por separado: antes se usaba forkJoin, que ante el primer
    // fallo aborta el conjunto, descarta tambien los resultados de las que SI habian
    // ido bien y -al no haber callback de error- dejaba el spinner girando para
    // siempre. Las sondas quedaban subidas en la base pero no en la pantalla.
    concat(...subidas.map(u => u.obs.pipe(
      tap((res: number) => this.assignToSensor(type, u.id, res)),
      catchError((err: unknown) => {
        console.error(err);
        fallos.push(u.id);

        return of(null);
      })
    ))).pipe(toArray(), takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.spinnerService.hide();

        if (fallos.length !== 0) {
          this.snackBarService.sendError(this.translate.instant('executionEdit.essays.form.uploadMassive.error',
            { fallos: fallos.length, total: subidas.length }) as string);
        }

        this.dialogRef.close(this.data.sensors);
      },
      error: (err: unknown) => {
        console.error(err);

        this.spinnerService.hide();
        this.snackBarService.sendError(this.translate.instant('executionEdit.essays.form.uploadMassive.error',
          { fallos: subidas.length, total: subidas.length }) as string);
      }
    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  private uploadOne(type: string, idSensor: number, file: File): Observable<number> {
    if (type === 'photo') {
      // fixImage trabaja con callback; se envuelve para poder encadenarla, y se le pasa
      // el onError para que un fichero ilegible sea un error del flujo -y se cuente
      // como fallo- en vez de un cuelgue silencioso.
      return new Observable<File>(subscriber => {
        PhotoUtils.fixImage(file, this.imageCompress, (res: File) => {
          subscriber.next(res);
          subscriber.complete();
        }, (err: unknown) => subscriber.error(err));
      }).pipe(switchMap((res: File) => this.attachmentThermalService.uploadPhotoToSensor(idSensor, res)));
    } else if (type === 'primary') {
      return this.attachmentThermalService.uploadPrimaryFileToSensor(idSensor, file);
    }

    return null;
  }

  private assignToSensor(type: string, id: string, res: number): void {
    const sensor = this.data.sensors.find(sen => sen.id === +id);

    if (sensor == null) {
      return;
    }

    if (type === 'photo') {
      sensor.idPhoto = Number(res);
    } else if (type === 'primary') {
      sensor.idPrimaryDataFile = Number(res);
    } else if (type === 'excel') {
      sensor.idExcel = Number(res);
    }
  }

}
